/* eslint-disable @typescript-eslint/member-ordering */
import { Observable } from 'rxjs';
import { debounceTime, filter, first, map, switchMap, withLatestFrom } from 'rxjs/operators';

import { FLAMINGO_RESERVED_REFS } from '@mode/shared/util-js';
import { Actions } from '~/actions';
import { AppStore } from '~/types/main.types';

import { FlamingoAdapter } from '@mode-switch/adapter-flamingo';
import { QueryBuilder } from '@mode/charts/util-vegas';
import { fromChartSelectors } from '@mode/charts/domain';
import { createEffect, ofType } from '@mode/legacy/util-domain';
import { DataViewFacade, FlamingoActions } from '@mode/switch-views/contract';
import { ChartDesignerActionType } from '~/actions/chart-designer/designer.actions';

const PAGING_DEBOUNCE_TIME = 150;

const adapter = new FlamingoAdapter();

// Effects that populate common data
export class FlamingoGfEffects {
  static $inject = ['RxStoreService', 'DataViewFacade', 'actions$'];

  constructor(
    private store: AppStore,
    private dataViewFacade: DataViewFacade,
    private actions$: Observable<Actions>
  ) {}

  // Table - Exploration Designer paging
  // Effect for GF routes
  explorationDesignerTablePaging$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChartDesignerActionType.SetPagingOffset, ChartDesignerActionType.SetPageSize),
      debounceTime(PAGING_DEBOUNCE_TIME),
      switchMap((action) =>
        this.store.selectNoEmpty(fromChartSelectors.createChartTypeEncodingSelector(action.vizToken)).pipe(
          filter(({ chartType }) => chartType === 'table'),
          first(),
          switchMap(
            ({ queryToken }) => this.dataViewFacade.createQueryRunTokenByQueryToken(queryToken),
            (data, runToken) => ({ ...data, runToken })
          ),
          withLatestFrom(
            this.dataViewFacade.model$,
            this.store.select(fromChartSelectors.createChartFormatSelector(action.vizToken))
          ),
          map(([chart, model, chartFormat]) => {
            const pageSize = chartFormat?.flatTable?.general?.pageSize;
            const offset = action.type === ChartDesignerActionType.SetPagingOffset ? action.offset : 0;
            return FlamingoActions.executePagePlan({
              plan: QueryBuilder.buildRecordsPlan(adapter, model, chart.queryToken, chart.encoding, pageSize, offset),
              refId: FLAMINGO_RESERVED_REFS.Exploration,
              queryRunToken: chart.runToken,
              allowRefresh: true,
            });
          })
        )
      )
    )
  );
}
